/**
 * /compare?ids=a,b,c — 2–5 experiments side by side: scores with changes
 * against a reference the user picks here (no baseline anywhere else), a
 * scatter, the items that got better or worse, and the config diff.
 *
 * Everything is URL state: `ref`, `tab`, `y`, `x`, `items` and `diff`, so a
 * comparison pasted into a discussion opens exactly as it was seen.
 */

import { sourceName } from "@/features/experiments/status";
import { ButtonLink } from "@/components/ui/button";
import { Crumbs, PageHeader } from "@/components/ui/page";
import { EmptyState, QueryState } from "@/components/ui/states";
import { Tabs } from "@/components/ui/tabs";
import { useUrlState } from "@/lib/use-url-state";
import { CompareChips } from "./compare-chips";
import { CompareConfig } from "./compare-config";
import { CompareItems } from "./compare-items";
import { CompareRadar, hasRadar } from "./compare-radar";
import { CompareScatter } from "./compare-scatter";
import { CompareScores } from "./compare-scores";
import { useComparison } from "./hooks";
import type { ItemFilter } from "./metrics";
import { MAX_COMPARE, MIN_COMPARE, parseIds } from "./selection";
import type { Comparison } from "./types";
import { VersionWarning } from "./version-warning";
import { Select } from "@/components/ui/select";
import { datasetLabel } from "@/features/experiments/dataset-label";

type Tab = "scores" | "items" | "config";
const ITEM_FILTERS: ItemFilter[] = ["all", "changed", "worse", "better"];

function DatasetLine({ data }: { data: Comparison }) {
  const first = data.experiments[0]?.dataset;
  if (data.same_dataset_version && first) {
    return (
      <span className="text-xs text-muted">
        All ran on {first.name} · v{first.version}, so items line up one to one.
      </span>
    );
  }
  const versions = [
    ...new Set(data.experiments.map((e) => (e.dataset ? datasetLabel(e.dataset, " · ") : "Trace run"))),
  ];
  return (
    <span role="note" className="rounded-md bg-warn-bg px-2 py-1 text-xs text-warn">
      Different dataset versions ({versions.join(", ")}): scores are not like for like, and items are not
      matched.
    </span>
  );
}

export function ComparePage() {
  const url = useUrlState();
  const ids = parseIds(url.get("ids"));
  const comparison = useComparison(ids);
  const refId = ids.includes(url.get("ref") ?? "") ? url.get("ref")! : (ids[0] ?? "");
  const tabParam = url.get("tab");
  const tab: Tab = tabParam === "items" || tabParam === "config" ? tabParam : "scores";
  const itemsParam = url.get("items") as ItemFilter | null;
  const filter: ItemFilter = itemsParam && ITEM_FILTERS.includes(itemsParam) ? itemsParam : "all";
  const setIds = (next: string[]) =>
    url.set({ ids: next.join(","), ref: next.includes(refId) ? url.get("ref") : null });

  if (ids.length < MIN_COMPARE || ids.length > MAX_COMPARE) {
    return (
      <EmptyState
        title="Pick two to five experiments to compare"
        action={<ButtonLink to="/experiments">Go to experiments</ButtonLink>}
      >
        Tick the experiments on the experiments list or an agent's page, then press Compare.
      </EmptyState>
    );
  }
  const data = comparison.data;
  const agents = [
    ...new Map((data?.experiments ?? []).map((e) => [e.agent?.id ?? "recorded", sourceName(e)])).entries(),
  ];
  const diffCount = data?.config_diff.filter((r) => r.differs).length;
  const [only] = agents;
  const parent =
    agents.length === 1 && only
      ? { label: only[1], to: `/agents/${only[0]}` }
      : { label: "Experiments", to: "/experiments" };

  // Capped like a document: past ~1200px the table and charts only gain white space.
  return (
    <div className="flex max-w-[1200px] flex-col gap-[18px]">
      <div className="flex flex-col gap-2.5">
        <Crumbs items={[{ label: "Home", to: "/" }, parent, { label: "Compare" }]} />
        <PageHeader
          title="Compare experiments"
          subtitle={agents.length > 1 ? `Agents: ${agents.map(([, n]) => n).join(", ")}` : undefined}
          actions={
            data && (
              <>
                <label htmlFor="cmp-ref" className="text-[13px] text-muted">
                  Show changes against
                </label>
                <Select
                  id="cmp-ref"
                  className="max-w-[280px]"
                  value={refId}
                  onChange={(ref) => url.set({ ref }, { replace: true })}
                  options={data.experiments.map((e) => ({ value: e.id, label: e.name }))}
                />
              </>
            )
          }
        />
      </div>
      <QueryState query={comparison} what="the comparison">
        {data && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <CompareChips
                experiments={data.experiments}
                onRemove={(id) => setIds(ids.filter((i) => i !== id))}
                onAdd={(id) => setIds([...ids, id])}
              />
              <span className="grow" />
              <DatasetLine data={data} />
            </div>
            <VersionWarning data={data} />
            <Tabs
              label="Compare sections"
              value={tab}
              onChange={(t) => url.set({ tab: t === "scores" ? null : t })}
              items={[
                { key: "scores", label: "Scores" },
                {
                  key: "items",
                  label: "Items",
                  count: data.same_dataset_version ? data.item_changes.length : undefined,
                },
                { key: "config", label: "Config", count: diffCount },
              ]}
            />
            {tab === "scores" && (
              <div className="flex flex-col gap-4">
                {/* Wide screens: the table and the radar side by side; narrow: stacked. */}
                <div
                  className={`grid items-start gap-4 ${hasRadar(data) ? "xl:grid-cols-[minmax(0,1fr)_400px]" : ""}`}
                >
                  <CompareScores
                    data={data}
                    refId={refId}
                    onOpenItems={(f, ev, vs) => url.set({ tab: "items", items: f, ev, vs })}
                  />
                  <CompareRadar data={data} />
                </div>
                <CompareScatter
                  data={data}
                  y={url.get("y") ?? "overall"}
                  x={url.get("x") ?? "cost"}
                  onAxes={(p: { y?: string; x?: string }) =>
                    url.set(
                      {
                        ...(p.y ? { y: p.y === "overall" ? null : p.y } : {}),
                        ...(p.x ? { x: p.x === "cost" ? null : p.x } : {}),
                      },
                      { replace: true },
                    )
                  }
                />
              </div>
            )}
            {tab === "items" && (
              <CompareItems
                data={data}
                refId={refId}
                filter={filter}
                onFilter={(f) => url.set({ items: f === "all" ? null : f }, { replace: true })}
                evaluator={url.get("ev")}
                vs={url.get("vs")}
                onClearScope={() => url.set({ ev: null, vs: null }, { replace: true })}
              />
            )}
            {tab === "config" && (
              <CompareConfig
                data={data}
                refId={refId}
                onlyDiff={url.get("diff") !== "0"}
                onToggle={(on) => url.set({ diff: on ? null : "0" }, { replace: true })}
              />
            )}
          </>
        )}
      </QueryState>
    </div>
  );
}
